// The property edit form is server-rendered, so what the user has typed but not saved exists only in
// the DOM. These read it back out so the generators can write from the form rather than from the
// record as it was last saved.

const PROPERTY_FIELDS = [
  'land_legal_description',
  'land_ownership',
  'lease_listed_on',
  'loan_description',
  'location_description',
  'name',
  'nearest_airport',
  'nearest_hightway',
  'range',
  'sale_listed_on',
  'section',
  'site_description',
];

const RESEARCH_FIELDS = [
  'address',
  'amenities',
  'apn',
  'best_use',
  'building_size',
  'ceilings',
  'city',
  'construction_description',
  'corridors',
  'county',
  'crane_description',
  'cross_streets',
  'dock_door_description',
  'dock_high_doors',
  'drive_in_bays',
  'easements_description',
  'elevators',
  'environmental_issues',
  'exterior_description',
  'exterior_walls',
  'floor_coverings',
  'foundation',
  'framing',
  'frontage',
  'gas_description',
  'grade_level_doors',
  'hvac',
  'interior_description',
  'irrigation_description',
  'landscaping',
  'latitude',
  'laundry_description',
  'load_factor',
  'loading_description',
  'longitude',
  'lot_depth',
  'market',
  'mezzanine',
  'number_of_buildings',
  'number_of_cranes',
  'number_of_elevators',
  'number_of_escalators',
  'number_of_lots',
  'number_of_parking_spaces',
  'number_of_units',
  'office_buildout',
  'overhead_door_height_f',
  'parking_description',
  'parking_price_per_month',
  'parking_ratio',
  'plumbing_description',
  'power_description',
  'restrooms',
  'retail_clientele',
  'roof',
  'sprinkler_description',
  'state',
  'stories',
  'submarket',
  'thomas_guide_page_number',
  'topography',
  'township',
  'traffic_count',
  'traffic_count_frontage',
  'traffic_count_street',
  'utilities_description',
  'walls',
  'warehouse_pct',
  'water_description',
  'year_built',
  'zip',
  'zoning',
];

// Rendered as selects of "yes" / "no" / blank rather than as checkboxes.
const PROPERTY_YES_NO_FIELDS = [
  'consider_exchange',
  'exchange_1031',
  'side_of_street',
  'signal_intersection',
  'street_parking',
];

const RESEARCH_YES_NO_FIELDS = [
  'cable',
  'centrix_equipped',
  'corner_property',
  'freestanding',
  'freight_elevator',
  'gas',
  'handicap_access',
  'irrigation',
  'leed_certified',
  'power',
  'rail_access',
  'security_guard',
  'sewer',
  'sprinklers',
  'telephone',
  'trailer_parking',
  'water',
  'waterfront',
];

const PROPERTY_SELECT_FIELDS = ['market_type', 'road_type', 'side_of_street'];
const RESEARCH_SELECT_FIELDS = ['broadband', 'building_class', 'condition', 'parking_type', 'soil_type'];

// Named differently on the form than in the record the generators write from.
const RENAMED_RESEARCH_FIELDS = {
  average_floor_size: 'typical_floor_size',
  ceiling_height_f: 'ceiling_height',
  ceiling_height_f_min: 'ceiling_height_min',
  electrical: 'power_description',
  occupancy: 'occupancy_pct',
  overhead_door_height_f: 'overhead_door_height',
  year_renovated: 'renovated',
};

export function readPropertyEditFormContext() {
  return { overrides: { property: readProperty() }, propertyAttributes: readPropertyAttributes() };
}

function readProperty() {
  const property: Record<string, unknown> = {
    property_type_id: domValue('#primary_property_type_id option:checked'),
    property_type_name: domText('#primary_property_type_id option:checked'),
    property_subtype_id: domValue('#primary_property_subtype_id option:checked'),
    property_subtype_name: domText('#primary_property_subtype_id option:checked'),

    sale_checked: domChecked('#property_sale_checked'),
    lease_checked: domChecked('#property_lease_checked'),
    foreclosure: domChecked(researchField('investment_type_distressed')),

    sale_title: domValue(saleListingField('title')),
    sale_description: domValue(saleListingField('description')),
    sale_bullets: readBullets('sale'),
    lease_title: domValue(leaseListingField('title')),
    lease_description: domValue(leaseListingField('description')),
    lease_bullets: readBullets('lease'),

    state_name: readStateName(),
  };

  for (const name of PROPERTY_FIELDS) {
    property[name] = domValue(propertyField(name));
  }

  for (const name of RESEARCH_FIELDS) {
    property[name] = domValue(researchField(name));
  }

  for (const [name, fieldName] of Object.entries(RENAMED_RESEARCH_FIELDS)) {
    property[name] = domValue(researchField(fieldName));
  }

  for (const name of PROPERTY_YES_NO_FIELDS) {
    property[name] = domYesNo(propertyField(name));
  }

  for (const name of RESEARCH_YES_NO_FIELDS) {
    property[name] = domYesNo(researchField(name));
  }

  for (const name of PROPERTY_SELECT_FIELDS) {
    property[name] = domValue(`${propertyField(name)} option:checked`);
  }

  for (const name of RESEARCH_SELECT_FIELDS) {
    property[name] = domValue(`${researchField(name)} option:checked`);
  }

  return property;
}

// The lease rate and gross area summaries are derived rather than typed, so the record has to
// recompute them from these instead of being handed a value.
function readPropertyAttributes() {
  const leaseSpaces = [...document.querySelectorAll('#leaseSpaceRows .leaseSpace')];

  return {
    measurements: domValue(researchField('measurements')),
    gross_building_area: domValue(researchField('gross_building_area')),
    lease_spaces_attributes: leaseSpaces.map(leaseSpace => ({
      _destroy: leaseSpaceValue(leaseSpace, '_destroy'),
      id: leaseSpaceValue(leaseSpace, 'id'),
      lease_rate: leaseSpaceValue(leaseSpace, 'lease_rate'),
      lease_rate_max: leaseSpaceValue(leaseSpace, 'lease_rate_max'),
      lease_rate_units: leaseSpaceValue(leaseSpace, 'lease_rate_units'),
      lease_type_id: leaseSpaceValue(leaseSpace, 'lease_type_id'),
      lease_type_label_override: leaseSpaceValue(leaseSpace, 'lease_type_label_override'),
      status: leaseSpaceValue(leaseSpace, 'status'),
    })),
  };
}

function propertyField(name: string) {
  return `#property_${name}`;
}

function researchField(name: string) {
  return `#property_research_property_attributes_${name}`;
}

function saleListingField(name: string) {
  return `#property_sale_listing_attributes_${name}`;
}

function leaseListingField(name: string) {
  return `#property_lease_listing_attributes_${name}`;
}

function readBullets(side: 'sale' | 'lease') {
  const value = domValue(`#property_${side}_agreement_attributes_bullets`);

  return String(value ?? '').split('\n').map(bullet => bullet.trim()).filter(Boolean);
}

// The option reads "IL - Illinois", and the record carries only the name.
function readStateName() {
  const label = document.querySelector<HTMLOptionElement>(`${researchField('state')} option:checked`)?.text;

  return label?.split(' - ')[1];
}

function leaseSpaceValue(leaseSpace: Element, name: string) {
  const inputs = [...leaseSpace.querySelectorAll<HTMLInputElement>('input')];
  const input = inputs.find(({ name: inputName }) => new RegExp(`\\[${name}\\]$`).test(inputName));

  return input?.value || undefined;
}

function domValue(selector: string) {
  const element = document.querySelector<HTMLInputElement>(selector);
  if (!element) {
    return null;
  }

  return element.type === 'number' && element.value !== '' ? parseFloat(element.value) : element.value || null;
}

function domText(selector: string) {
  return document.querySelector<HTMLOptionElement>(selector)?.text || null;
}

function domChecked(selector: string) {
  return document.querySelector<HTMLInputElement>(selector)?.checked ?? null;
}

function domYesNo(selector: string) {
  const value = document.querySelector<HTMLSelectElement>(selector)?.querySelector('option:checked');

  return value instanceof HTMLOptionElement && ['yes', 'no'].includes(value.value) ? value.value === 'yes' : null;
}
